import React from 'react';
import { CheckCircle } from 'lucide-react';

/**
 * Step progress bar used across the provider onboarding wizard.
 *
 * Mobile (< 640px):
 *   Compact "Step X of N" header + a thin progress bar. Seven 40px circles
 *   plus connectors overflowed 430px on a 375px iPhone and caused horizontal
 *   scrolling, so on small screens we swap the whole thing out for a
 *   lightweight summary that still shows progress at a glance.
 *
 * Tablet + desktop (>= 640px):
 *   The full numbered-circle + connector + label row. Unchanged from before.
 */
export function StepProgress({ step, steps }) {
  const currentStep = steps.find((s) => s.number === step);
  const totalSteps = steps.length;
  const progressPercent = totalSteps > 1 ? ((step - 1) / (totalSteps - 1)) * 100 : 0;

  return (
    <div className="mb-6 sm:mb-8" data-testid="onboarding-step-progress">
      {/* Mobile: compact header + progress bar. */}
      <div className="sm:hidden">
        <div className="flex items-baseline justify-between mb-2">
          <p className="text-sm font-medium text-[#1A1A1A]">
            {currentStep ? currentStep.title : ''}
          </p>
          <p className="text-xs text-stone-500">
            Step {step} of {totalSteps}
          </p>
        </div>
        <div className="h-1.5 rounded-full bg-[#E6E4DD] overflow-hidden">
          <div
            className="h-full bg-[#D4AF37] transition-all duration-300"
            style={{ width: `${progressPercent}%` }}
            data-testid="onboarding-progress-bar"
          />
        </div>
      </div>

      {/* Tablet + desktop: full numbered-circle row. */}
      <div className="hidden sm:flex items-center justify-between mb-4">
        {steps.map((s, i) => (
          <React.Fragment key={s.number}>
            <div className="flex items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
                  step >= s.number
                    ? 'bg-[#D4AF37] text-white'
                    : 'bg-[#E6E4DD] text-stone-500'
                }`}
              >
                {step > s.number ? <CheckCircle className="h-5 w-5" /> : s.number}
              </div>
              <span
                className={`ml-2 hidden md:block text-sm ${
                  step >= s.number ? 'text-[#1A1A1A]' : 'text-stone-500'
                }`}
              >
                {s.title}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div
                className={`flex-1 h-1 mx-2 lg:mx-4 rounded ${
                  step > s.number ? 'bg-[#D4AF37]' : 'bg-[#E6E4DD]'
                }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
